/**
 * Token rename fix — report side.
 *
 * Access scans store the token name that matched AT SCAN TIME, so after a
 * rename the same token appears under two strings ("GA" rows before, and
 * "General Admission" rows after). This helper remaps every row's
 * access_token_type to the token's CURRENT name using the registry + alias
 * tables, so grouping/filtering shows one combined line under the new name.
 *
 * Display-only and best-effort: rows are never mutated in the DB, the raw
 * string is preserved on `access_token_type_raw`, and any failure (e.g.
 * registry tables not set up yet) returns the rows unchanged.
 */
import { gql } from "@apollo/client";

const GET_TOKENS_FOR_EVENTS = gql`
  query redeemableTokensForReport($eventIds: [Int!]!) {
    redeemable_tokens(where: { event_id: { _in: $eventIds } }) {
      id
      event_id
      name
      aliases {
        alias
      }
    }
  }
`;

const norm = (s: unknown): string =>
  typeof s === "string" ? s.trim().toLowerCase() : "";

type Row = { event_id?: number; access_token_type?: string | null } & Record<
  string,
  unknown
>;

export async function remapAccessTokenTypes<T extends Row>(
  client: { query: Function },
  rows: T[]
): Promise<T[]> {
  try {
    if (!rows?.length) return rows;

    const eventIds = [
      ...new Set(rows.map((r) => r.event_id).filter((id) => id != null)),
    ];
    if (!eventIds.length) return rows;

    const { data } = await client.query({
      query: GET_TOKENS_FOR_EVENTS,
      variables: { eventIds },
      fetchPolicy: "network-only",
    });
    const tokens: Array<{
      event_id: number;
      name: string;
      aliases: { alias: string }[];
    }> = data?.redeemable_tokens || [];
    if (!tokens.length) return rows;

    // Per-event lookup: norm(name | alias) -> current name.
    // Live names are inserted first so an alias can never shadow one.
    const byEvent = new Map<number, Map<string, string>>();
    const eventMap = (id: number) => {
      let m = byEvent.get(id);
      if (!m) {
        m = new Map();
        byEvent.set(id, m);
      }
      return m;
    };
    tokens.forEach((t) => {
      const key = norm(t.name);
      if (key) eventMap(t.event_id).set(key, t.name);
    });
    tokens.forEach((t) => {
      (t.aliases || []).forEach((a) => {
        const key = norm(a.alias);
        const m = eventMap(t.event_id);
        if (key && !m.has(key)) m.set(key, t.name);
      });
    });

    return rows.map((r) => {
      if (r.event_id == null) return r;
      const current = byEvent.get(r.event_id)?.get(norm(r.access_token_type));
      if (!current || current === r.access_token_type) return r;
      return {
        ...r,
        access_token_type: current,
        access_token_type_raw: r.access_token_type,
      };
    });
  } catch (err) {
    console.warn("[tokenNameRemap] skipped:", err);
    return rows;
  }
}
